import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectDispatch } from '@ngrx/signals/events';
import { MultiTabSyncService, TabSyncMessage, TabSyncMessageType, AuthLockedPayload } from './multi-tab-sync.service';
import { AppStore, AuthStore } from '@app/core/states';
import { NotificationService } from './notification.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { DekError } from '@app/domain/entities';
import {
  ACTIVE_SQL_MODE,
  DATABASE_PERSISTENCE_ADAPTER,
  DATABASE_SERVICE,
} from '@app/core/tokens/infrastructure.tokens';
import { DataSyncEvents } from '@app/domain/events';
import { TranslateService } from '@ngx-translate/core';

/**
 * Coordinates multi-tab synchronization by handling broadcast messages
 * and reloading stores when database changes occur in other tabs.
 */
@Injectable({ providedIn: 'root' })
export class TabSyncCoordinatorService {
  private readonly multiTabSync = inject(MultiTabSyncService);
  private readonly appStore = inject(AppStore);
  private readonly authStore = inject(AuthStore);
  private readonly databaseService = inject(DATABASE_SERVICE);
  private readonly sqliteMode = inject(ACTIVE_SQL_MODE) as 'straight' | 'worker' | 'opfs' | 'native';
  private readonly notificationService = inject(NotificationService);
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  private readonly dbLoader = inject(DATABASE_PERSISTENCE_ADAPTER);
  private readonly dispatch = injectDispatch(DataSyncEvents);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  private isReloading = false;

  initialize(): void {
    // Register handlers immediately to ensure we never miss events (especially AUTH_LOCKED)
    this.multiTabSync
      .onMessage(TabSyncMessageType.DATABASE_CHANGED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleDatabaseChanged();
      });

    this.multiTabSync
      .onMessage(TabSyncMessageType.AUTH_LOCKED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => {
        this.handleAuthLocked(message);
      });

    this.multiTabSync
      .onMessage(TabSyncMessageType.RELOAD_REQUEST)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleDatabaseChanged();
      });
  }

  /**
   * Handle database changes broadcast from leader tab.
   * Implements Option A: Full Reload Pattern
   * 1. Reload SQLite database from IndexedDB
   * 2. Reload all stores from fresh SQLite data
   */
  private async handleDatabaseChanged(): Promise<void> {
    // Ignore if database not initialized yet
    if (!this.appStore.isDatabaseInitialized()) return;
    if (this.isReloading) return;

    // Check for open dialogs (unsaved work)
    const hasOpenDialogs = this.dialogService.dialogComponentRefMap.size > 0;
    if (hasOpenDialogs) {
      this.notificationService.warn(
        this.translate.instant('common.notifications.sync_pending_message'),
        this.translate.instant('common.notifications.sync_pending_header'),
      );
      return;
    }

    try {
      this.isReloading = true;

      const reloadSource =
        this.sqliteMode === 'opfs' ? 'OPFS' : this.sqliteMode === 'native' ? 'native SQLite' : 'IndexedDB';
      console.log(`🔄 Reloading SQLite database from ${reloadSource}...`);

      await this.reloadDatabaseAfterRemoteChange();

      console.log('✓ Database reloaded successfully');

      // Show subtle notification
      this.notificationService.info(
        this.translate.instant('common.notifications.synced_message'),
        this.translate.instant('common.notifications.synced_header'),
      );
    } catch (error) {
      console.error('Failed to reload database and stores:', error);
      this.notificationService.error(
        this.translate.instant('common.notifications.sync_error_message'),
        this.translate.instant('common.notifications.sync_error_header'),
      );
    } finally {
      this.isReloading = false;
    }
  }

  /**
   * Reload the active SQLite connection from the authoritative persistence source.
   * IndexedDB is authoritative in worker/straight modes; OPFS is authoritative in opfs mode.
   */
  private async reloadDatabaseAfterRemoteChange(): Promise<void> {
    if (this.sqliteMode === 'opfs' || this.sqliteMode === 'native') {
      await this.reinitializeDatabaseFromOpfs();
    } else {
      const data = await this.dbLoader.load();
      if (!data) throw new DekError('reload-database', 'No database found in IndexedDB');

      // Import fresh data into SQLite
      await this.databaseService.importDatabase(data);
    }

    this.dispatch.databaseReloaded();
  }

  /**
   * Re-open the shared OPFS-backed database so this tab sees changes committed by another tab.
   */
  private async reinitializeDatabaseFromOpfs(): Promise<void> {
    await this.databaseService.close();
    await this.databaseService.initialize({ runMigrations: true });
  }

  /**
   * Handle authentication lock event from another tab.
   * Locks this tab and navigates to login only when the reason is NOT inactivity.
   * Inactivity-triggered locks are local to the tab that went idle — each tab
   * manages its own idle timer independently.
   */
  private async handleAuthLocked(message: TabSyncMessage): Promise<void> {
    const payload = message.payload as AuthLockedPayload | undefined;
    const reason = payload?.reason ?? 'manual';

    if (reason === 'inactivity') {
      console.log(
        '⏭ Skipping cross-tab lock: remote tab timed out due to inactivity (this tab manages its own idle timer)',
      );
      return;
    }

    console.log('🔒 Auth locked in another tab - locking this tab');
    this.notificationService.info(
      this.translate.instant('common.notifications.logged_out_message'),
      this.translate.instant('common.notifications.logged_out_header'),
    );

    await this.authStore.logout();
    await this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.routerState.snapshot.url } });
  }
}
